import { ElementRef, Injectable } from '@angular/core';
import { ThreeSixtyViewer } from './360viewerLibrary/ThreeSixtyViewer';
import { Project360Service } from '../p-dashboard/project360.service';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Floor, PointImage, ProjectDetails, Report } from 'app/models/project.model';
import { RestfulEndpoint } from 'app/services/restful-endpoint.service';
import { ProjectsService } from 'app/services/projects.service';
import { RefactoredReport } from 'app/models/reports-refactored.model';
import { TranslocoService } from '@ngneat/transloco';
import { ToastrService } from 'ngx-toastr';
import { AccessLevel } from '../../core/user/user.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectsRefactoredService } from '../../services/Projects/projects-refactored.service';
import { GlobalService } from '../../services/global.service';
import { GAnalyticsService } from '../../services/g-analytics.service';

import { MapPoint } from './models/map-model';
import { NavigationHotspot } from './models/NavigationHotspot';
import { ViewerBimServiceService } from './viewer-bim-service.service';
import * as moment from 'moment-timezone';
import { ViewerScreenDirection, onCameraMoveCallback } from './360viewerLibrary/Types';
import { ViewerModeService } from './viewer-mode.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FLOOR_STATUS, REPORT_TYPE } from '../project/const';
import { DemoViewerApiService } from './demo-viewer-api.service';
import { DemoPointsService } from './demo-points.service';
import { PointMatchService } from './point-match.service';
import { WalkthroughService } from './walkthrough.service';
import { Walkthrough } from './360viewerLibrary/ViewerElement/WalkthroughElement';
import { ScreenshotService } from './screenshot.service';
import * as THREE from 'three';
import { PendingReportFloor } from '../operation-section/pending-reports-models';
import { TourGuideDialogComponent } from '../projects/tour-guide-dialog/tour-guide-dialog.component';
import { TourGuideModel } from 'app/models/tour-guide.model';
import { SaveBimOffsetDialogComponent } from 'app/shared/components/save-bim-offset-dialog/save-bim-offset-dialog.component';
import { UserService } from 'app/core/user/user.service';

import { TimelapseFrame } from './models/timelapse.models';
import { TimelapseSequenceFrame, TimelapseSequenceManager } from './360viewerLibrary/timelapse';
@Injectable({
  providedIn: 'root',
})
export class DemoProjectService {
  public threeSixtyViewer: ThreeSixtyViewer;
  public reports: any[] = [];
  private isReportsLoading = false;
  private isLeftReportsLoading = false;
  public currentRightPointImageId?: string;
  public currentRightPointImage?: MapPoint;
  public currentRightPointImageReportType?: string;
  public currentRightPointImageReportId?: string;

  // loading flag for the viewer
  public isLoadingTextures = false;
  private baseLoadingStateCallback?: (isLoading: boolean) => void;
  private textureLoadingStateCallback?: (isLoading: boolean) => void;
  private viewerSettings: Record<string, unknown> = {};
  private isBaseTileLoading = false;
  private screenLoadingCounter = 0;

  public projectId: string | null = null;
  public defaultReportId: string = '';
  public defaultFloorId: string;
  public defaultPointId: string;
  public selectedFloor: Floor | PendingReportFloor;
  public isFloorDetailsLoading: boolean = false;
  public selectedReport: RefactoredReport;
  public selectedLeftReport: RefactoredReport;
  public projectDetails: ProjectDetails;
  public projectReports: RefactoredReport[] = [];
  public leftVideoReports: RefactoredReport[] = [];
  public leftImageReports: RefactoredReport[] = [];

  private bimCameraSyncCallBack: onCameraMoveCallback;

  private initialHorizAngleDeg: number | null = null;
  reportsMeta: any;
  leftVideoReportsMeta: any;
  leftImageReportsMeta: any;

  private readonly timelapseSequenceManager = new TimelapseSequenceManager();
  private timelapseSequenceFrames: TimelapseSequenceFrame[] = [];
  private timelapseFrameIndexMap = new Map<string, number>();
  private selectedFloorSubject = new BehaviorSubject<Floor | PendingReportFloor | null>(null);
  selectedFloor$ = this.selectedFloorSubject.asObservable();

  private selectedReportSubject = new BehaviorSubject<Report | null>(null);
  selectedReport$ = this.selectedReportSubject.asObservable();

  private selectedLeftReportSubject = new BehaviorSubject<Report | null>(null);
  selectedLeftReport$ = this.selectedLeftReportSubject.asObservable();

  constructor(
    public projectApi: ProjectsService,
    private endPoint: RestfulEndpoint,
    private _translocoService: TranslocoService,
    private _toaster: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    public _project: Project360Service,
    public _projectRefactored: ProjectsRefactoredService,
    public googleServices: GAnalyticsService,
    private bimService: ViewerBimServiceService,
    private global: GlobalService,
    private viewerMode: ViewerModeService,
    public dialog: MatDialog,
    private api: DemoViewerApiService,
    private demoPointsService: DemoPointsService,
    private pointMatchService: PointMatchService,
    private walkthroughService: WalkthroughService,
    private screenshotService: ScreenshotService,
    private userService: UserService,
  ) {}

  async initTheData() {
    if (this.projectId) {
      await this.initData();
    } else {
      this.global.handleErrors('Project ID is null');
    }
  }
  private async initData(): Promise<void> {
    if (!this.projectId) return;

    await this.fetchProjectDetails();
    await this.fetchProjectMainReports();
    this.demoPointsService.init(this.projectDetails.default_point_id, this.defaultReportId);
    await this.setDefaultReport(this.defaultReportId);
    await this.setDefaultFloor();
  }

  // operation section
  async initPendingReportData(projectID: string, pendingReport: any) {
    if (this._project.isOperationEngineer()) {
      // current report
      this.selectedReport = pendingReport;
      this.selectedLeftReport = pendingReport;
      this.projectReports = [pendingReport];
      this._project.selectedReport = this.selectedReport;

      // ready floors
      let newFloors = [];
      let readyfloor;
      this._project.floors.forEach((floor) => {
        let includedFloor = pendingReport.floors.find((rf) => rf.floor_id == floor.id);
        if (includedFloor) {
          floor.is_checked = true;
          includedFloor.is_checked == true ? (floor.is_checked = true) : (floor.is_checked = false);
          floor.status = includedFloor?.status || null;
          floor.video_points = includedFloor.video_points;
          floor.isIncluded = true;
          newFloors.push(floor);
          floor.status == FLOOR_STATUS.READY_FOR_OPERATION_ENG
            ? (readyfloor = floor)
            : (readyfloor = newFloors[0]);
        }
      });
      // update floors
      this._project.setFloors(newFloors);
      this.projectDetails = this._project.projectInfo;
      this.projectDetails.floors = newFloors;
      this.syncViewerSettingsFromProject();

      this.selectedFloor = readyfloor;
      this.demoPointsService.pointImages = (this.selectedFloor as PendingReportFloor).video_points;
      this.selectedFloorSubject.next(this.selectedFloor);
    }
  }

  populateAdjustedFloor(fl) {
    const floorIndex = this.projectDetails.floors.findIndex((f) => f.id === fl.floor_id);
    if (floorIndex !== -1) {
      this.projectDetails.floors[floorIndex] = fl;
      this.demoPointsService.pointImages = fl.video_points;
      this.setSelectedFloor(fl);
    }
  }

  async setSelectedFloor(floor: Floor | PendingReportFloor) {
    // Normalize PendingReportFloor to have an id so downstream code (expecting .id) keeps working
    if (!(floor as any).id && (floor as any).floor_id) {
      (floor as any).id = (floor as any).floor_id;
    }
    this.selectedFloor = floor;

    this.bimService.currentFloor = floor as Floor;
    await this.updateQueryParams({ floor_id: floor.id });

    const leftReport =
      this.selectedLeftReport.id !== this.selectedReport.id
        ? this.selectedLeftReport
        : this.selectedReport;

    const floor_status = (floor as Floor)?.status;
    if (
      floor_status &&
      floor_status === 'PUBLISHED_BY_OPERATION_ENG' &&
      !this._project.isOperationEngineer()
    ) {
      await Promise.all([
        this.demoPointsService.updateMainPointImages(floor.id, this.selectedReport),
        this.demoPointsService.updateLeftPointImages(floor.id, leftReport),
      ]);
      if (this.selectedReport.report_type === REPORT_TYPE.VIDEOS) {
        this.pointMatchService.updatePointMatches(this.selectedReport.id, this.selectedFloor.id);
        this.walkthroughService.updateWalkthroughs(this.selectedReport.id, this.selectedFloor.id);
      }
    } else {
      this.demoPointsService.updatePendingReportPoints(floor as PendingReportFloor);
    }

    this.selectedFloorSubject.next(floor);
  }

  async setSelectedReport(report: RefactoredReport) {
    this.selectedReport = report;
    this.bimService.currentReport = report;
    this.defaultReportId = report.id;

    await this.demoPointsService.updateMainPointImages(this.selectedFloor.id, report);
    await this.updateQueryParams({ report_id: report.id });
    this.selectedReportSubject.next(report);
    this.getIncludedFloor();
    await this.initLeftReports();
    // Only sync left report when split screen is NOT enabled
    // When split screen is enabled, reports should be independent
    if (!this.viewerMode.isSplitScreenOpened) {
      await this.setSelectedLeftReport(report);
    }
    // If split screen is enabled and report types are different, we still need to update left report
    // to maintain type consistency (video reports on left must match video reports on right)
    else if (this.selectedLeftReport?.report_type !== this.selectedReport.report_type) {
      await this.setSelectedLeftReport(report);
    }
    this.updateWalkthroughsAndPointMatches();
  }
  updateWalkthroughsAndPointMatches() {
    if (this.selectedReport.report_type === REPORT_TYPE.VIDEOS) {
      this.pointMatchService.updatePointMatches(this.selectedReport.id, this.selectedFloor.id);
      this.walkthroughService.updateWalkthroughs(this.selectedReport.id, this.selectedFloor.id);
    }
  }
  async initLeftReports() {
    if (
      this.selectedReport?.report_type === REPORT_TYPE.VIDEOS &&
      this.leftVideoReports.length === 0
    ) {
      await this.fetchProjectLeftVideoReports();
      if (!this.leftVideoReports.some((r) => r.id === this.selectedReport.id))
        this.leftVideoReports.push(this.selectedReport);
      this.leftVideoReports = this.leftVideoReports.filter(
        (person, index, self) => index === self.findIndex((p) => p.id === person.id),
      );
    } else if (
      this.selectedReport?.report_type === REPORT_TYPE.IMAGE &&
      this.leftImageReports.length === 0
    ) {
      await this.fetchProjectLeftImageReports();
      if (!this.leftImageReports.some((r) => r.id === this.selectedReport.id))
        this.leftImageReports.push(this.selectedReport);
      this.leftImageReports = this.leftImageReports.filter(
        (person, index, self) => index === self.findIndex((p) => p.id === person.id),
      );
    }
  }
  getIncludedFloor() {
    let newFloors = [];
    this.projectDetails.floors.forEach((floor) => {
      let includedFloor = this.selectedReport?.report_floors.find((rf) => rf.floor_id == floor.id);

      floor.isIncluded = !!includedFloor;
      floor.status = includedFloor?.status || null;

      newFloors.push(floor);
    });
    this.projectDetails.floors = newFloors;
  }

  private addReportToLeftReports(report: RefactoredReport) {
    if (
      this.selectedReport?.report_type === REPORT_TYPE.VIDEOS &&
      !this.leftVideoReports.some((r) => r.id === report.id)
    ) {
      this.leftVideoReports.unshift(report);
    } else if (
      this.selectedReport?.report_type === REPORT_TYPE.IMAGE &&
      !this.leftImageReports.some((r) => r.id === report.id)
    ) {
      this.leftImageReports.unshift(report);
    }
  }
  async setSelectedLeftReport(report: RefactoredReport) {
    this.addReportToLeftReports(report);
    this.selectedLeftReport = report;

    // Update the left point images for the split map
    await this.demoPointsService.updateLeftPointImages(this.selectedFloor.id, report);

    // Notify subscribers that the left report has changed
    this.selectedLeftReportSubject.next(report);
  }

  async fetchProjectDetails(): Promise<void> {
    if (this.projectId) {
      const res = await this.projectApi.getProjectDetails(this.projectId).toPromise();

      if (res) {
        this.projectDetails = res.project_details;

        this._project.projectDetails = res.project_details;
        this._project.projectId = this.projectId;
        this.syncViewerSettingsFromProject();
      }
    }
  }

  async fetchProjectMainReports(page: number = 1) {
    try {
      const response = await lastValueFrom(
        this._projectRefactored.getProjectReportsRefactored(this.projectId, 10, page),
      );
      this.projectReports = this.projectReports.concat(response.data);
      this.reportsMeta = response.meta_data;
    } catch (error) {
      this.global.handleErrors(error);
    }
  }
  async fetchProjectLeftVideoReports(page: number = 1) {
    try {
      const response = await lastValueFrom(
        this._projectRefactored.getProjectReportsRefactored(
          this.projectId,
          10,
          page,
          REPORT_TYPE.VIDEOS,
        ),
      );
      this.leftVideoReports = this.leftVideoReports.concat(response.data);
      this.leftVideoReports = this.leftVideoReports.filter(
        (person, index, self) => index === self.findIndex((p) => p.id === person.id),
      );
      this.leftVideoReportsMeta = response.meta_data;
    } catch (error) {
      this.global.handleErrors(error);
    }
  }
  async fetchProjectLeftImageReports(page: number = 1) {
    try {
      const response = await lastValueFrom(
        this._projectRefactored.getProjectReportsRefactored(
          this.projectId,
          10,
          page,
          REPORT_TYPE.IMAGE,
        ),
      );
      this.leftImageReports = this.leftImageReports.concat(response.data);
      this.leftImageReports = this.leftImageReports.filter(
        (person, index, self) => index === self.findIndex((p) => p.id === person.id),
      );
      this.leftImageReportsMeta = response.meta_data;
    } catch (error) {
      this.global.handleErrors(error);
    }
  }

  async loadReportsOnScroll() {
    if (
      !this.viewerMode.isLoading &&
      !this.isReportsLoading &&
      this.reportsMeta.total_pages > this.reportsMeta.current_page
    ) {
      try {
        this.isReportsLoading = true;
        let page = this.reportsMeta.current_page + 1;
        await this.fetchProjectMainReports(page);
      } finally {
        this.isReportsLoading = false;
      }
    }
  }
  async loadLeftReportsOnScroll(reportType: string) {
    if (reportType === REPORT_TYPE.VIDEOS) {
      const totalPages =
        this.leftVideoReportsMeta == undefined ? 0 : this.leftVideoReportsMeta.total_pages;
      const currentPage =
        this.leftVideoReportsMeta == undefined ? -1 : this.leftVideoReportsMeta.current_page;
      if (!this.viewerMode.isLoading && !this.isLeftReportsLoading && totalPages > currentPage) {
        try {
          this.isLeftReportsLoading = true;
          let page = currentPage + 1;
          await this.fetchProjectLeftVideoReports(page);
        } finally {
          this.isLeftReportsLoading = false;
        }
      }
    } else if (reportType === REPORT_TYPE.IMAGE) {
      const totalPages =
        this.leftImageReportsMeta == undefined ? 0 : this.leftImageReportsMeta.total_pages;
      const currentPage =
        this.leftImageReportsMeta == undefined ? -1 : this.leftImageReportsMeta.current_page;
      if (!this.viewerMode.isLoading && !this.isLeftReportsLoading && totalPages > currentPage) {
        try {
          this.isLeftReportsLoading = true;
          let page = currentPage + 1;
          await this.fetchProjectLeftImageReports(page);
        } finally {
          this.isLeftReportsLoading = false;
        }
      }
    }
  }

  /**
   * Seed the timelapse sequence manager with the current timeline frames.
   */
  initializeTimelapseSequence(frames: TimelapseFrame[], startIndex = 0): void {
    this.timelapseFrameIndexMap.clear();
    this.timelapseSequenceFrames = (frames ?? []).map((frame, index) => {
      this.timelapseFrameIndexMap.set(frame.id, index);
      return this.toTimelapseSequenceFrame(frame);
    });

    this.timelapseSequenceManager.setFrames(this.timelapseSequenceFrames, startIndex);
  }

  /**
   * Display a timelapse frame and advance prefetch windows accordingly.
   */
  async displayTimelapseFrame(frame: TimelapseFrame | null | undefined): Promise<void> {
    if (!frame) {
      return;
    }

    const currentOrientation = this.threeSixtyViewer?.getCameraOrientation
      ? this.threeSixtyViewer.getCameraOrientation()
      : undefined;

    const sequenceFrame = this.toTimelapseSequenceFrame(frame);
    let frameIndex = this.timelapseFrameIndexMap.get(frame.id);
    if (frameIndex === undefined || frameIndex < 0) {
      this.timelapseSequenceFrames.push(sequenceFrame);
      frameIndex = this.timelapseSequenceFrames.length - 1;
      this.timelapseFrameIndexMap.set(frame.id, frameIndex);
    } else {
      this.timelapseSequenceFrames[frameIndex] = sequenceFrame;
    }

    this.timelapseSequenceManager.ensureFrameReady(frameIndex);

    await this.loadTimelapseSequenceFrame(sequenceFrame, currentOrientation);
    this.timelapseSequenceManager.advanceWindow(frameIndex);
  }

  /**
   * Clear any cached timelapse sequence data (e.g., when exiting timelapse mode).
   */
  clearTimelapseSequence(): void {
    this.timelapseSequenceManager.clear();
    this.timelapseSequenceFrames = [];
    this.timelapseFrameIndexMap.clear();
  }

  private toTimelapseSequenceFrame(frame: TimelapseFrame): TimelapseSequenceFrame {
    return {
      id: frame.id,
      imageUrl: frame.imageUrl,
      thumbnailUrl: frame.thumbnailUrl,
      tiles: frame.tiles,
      tileSourceMode: frame.tileSourceMode,
      initialOrientation: frame.initialOrientation,
    };
  }

  private async loadTimelapseSequenceFrame(
    frame: TimelapseSequenceFrame,
    orientationOverride?: TimelapseSequenceFrame['initialOrientation'],
  ): Promise<void> {
    if (!this.threeSixtyViewer) {
      return;
    }

    const tileSourceMode =
      frame.tileSourceMode ?? (this.isBlurFacesEnabled() ? 'blurred' : 'standard');
    const rightPayload: {
      image_360?: string;
      image_360_thumb?: string;
      tiles?: TimelapseSequenceFrame['tiles'];
      tileSourceMode?: 'standard' | 'blurred';
    } = {
      image_360: frame.imageUrl,
      tileSourceMode,
    };

    if (frame.thumbnailUrl) {
      rightPayload.image_360_thumb = frame.thumbnailUrl;
    }

    if (frame.tiles && frame.tiles.length > 0) {
      rightPayload.tiles = frame.tiles;
    }

    const mergedAngle = {
      horizontalAngle:
        orientationOverride?.horizontalAngle ?? frame.initialOrientation?.horizontalAngle,
      verticalAngle: orientationOverride?.verticalAngle ?? frame.initialOrientation?.verticalAngle,
    };
    const hasOrientation =
      mergedAngle.horizontalAngle !== undefined || mergedAngle.verticalAngle !== undefined;
    const angle = hasOrientation ? mergedAngle : undefined;

    const isTiled = !!(rightPayload.tiles && rightPayload.tiles.length > 0);
    const hasThumb = !!rightPayload.image_360_thumb;

    if (!isTiled) {
      const targetUrl = hasThumb ? rightPayload.image_360_thumb : rightPayload.image_360;
      if (!targetUrl) {
        await this.threeSixtyViewer.loadImage('empty');
        return;
      }

      await this.threeSixtyViewer.loadImage(targetUrl, undefined, angle);
      return;
    }

    await this.threeSixtyViewer.loadImage(rightPayload, undefined, angle);
  }

  async loadMapPointIntoViewer(mapPoint: MapPoint | null | undefined): Promise<void> {
    if (!this.threeSixtyViewer || !mapPoint) {
      return;
    }

    const imageUrl = mapPoint.image_360_url;
    const thumbUrl = mapPoint.image_360_thumb;
    const hasImage = imageUrl && imageUrl !== 'empty';

    if (!hasImage) {
      await this.threeSixtyViewer.loadImage('empty');
      return;
    }

    const payload: {
      image_360?: string;
      image_360_thumb?: string;
      tiles?: TimelapseSequenceFrame['tiles'];
      tileSourceMode?: 'standard' | 'blurred';
    } = {
      image_360: imageUrl,
      tileSourceMode:
        mapPoint.tileSourceMode ?? (this.isBlurFacesEnabled() ? 'blurred' : 'standard'),
    };

    if (thumbUrl && thumbUrl !== 'empty') {
      payload.image_360_thumb = thumbUrl;
    }

    const tilesToUse = mapPoint.tiles;
    if (tilesToUse && tilesToUse.length > 0) {
      payload.tiles = tilesToUse;
    }

    await this.threeSixtyViewer.loadImage(payload, undefined, mapPoint.angle);
  }

  async takeScreenShot(): Promise<void> {
    await this.screenshotService.takeScreenshot(
      this.threeSixtyViewer,
      this.bimService,
      this.viewerMode.isBimSplit,
    );
  }

  async toggleSplitScreen() {
    await this.threeSixtyViewer.toggleSplitScreen();
    this.viewerMode.toggleSplitScreen();

    if (this.viewerMode.isSplitScreen) {
      await this.initLeftReports();
      const reportToSet = this.selectedLeftReport || this.selectedReport;
      await this.setSelectedLeftReport(reportToSet);
    } else {
      this.viewerMode.isLoading = false;
    }
  }
  private async setDefaultFloor() {
    let report = await this.api.getReportDefaultFloor(this.defaultReportId).toPromise();

    this.defaultFloorId = report.data.floor_id;
    this.defaultPointId = report.data.image_id;

    let floor =
      this.projectDetails.floors.find((f) => f.id === this.defaultFloorId) ||
      this.projectDetails.floors[0];
    if (floor) {
      this.defaultFloorId = floor.id;
      await this.setSelectedFloor(floor);
    }
  }
  private async setDefaultReport(reportId: string) {
    const reportIdToFind = reportId || this.projectDetails.Last_report_id;
    this.selectedReport = this.projectReports.find((f) => f.id === reportIdToFind);
    if (!this.selectedReport) {
      try {
        const response = await lastValueFrom(
          this.api.getProjectReportsRefactoredByReportId(this.projectId, reportIdToFind),
        );
        this.selectedReport = response.data[0];

        const addObjectIfNotExists = (arr, obj) => {
          if (!arr.some((item) => item.id === obj.id)) {
            arr.push(obj);
          }
        };
        addObjectIfNotExists(this.projectReports, this.selectedReport);
      } catch (error) {
        this.global.handleErrors(error);
      }
    }
    if (this.viewerMode.isSplitScreen) await this.initLeftReports();

    this.selectedLeftReport = this.selectedReport;
    this.defaultReportId = reportIdToFind;
    await this.updateQueryParams({ report_id: this.selectedReport?.id });
    this.getIncludedFloor();
    this.selectedReportSubject.next(this.selectedReport);
  }

  public initialize360Viewer(canvasContainer: ElementRef): void {
    if (canvasContainer) {
      this.threeSixtyViewer = new ThreeSixtyViewer(canvasContainer.nativeElement);

      this.threeSixtyViewer.setWatermarkEnabled(false);
      this.setupTextureLoadingTracking();
      this.applyViewerSettings();
    }
    this.bimCameraSyncCallBack = (target) => {
      const bimOffset = this.currentRightPointImage?.bim_offset || 0;
      this.bimService.syncBimCameraWith360Viewer(target, bimOffset);
    };
  }

  /**
   * Setup texture loading state tracking
   * - baseLoadingStateCallback: controls main loading panel (only during initial base tile load)
   * - textureLoadingStateCallback: controls white spinner (during any tile loading, including LOD transitions)
   */
  private setupTextureLoadingTracking(): void {
    if (!this.threeSixtyViewer) return;

    this.viewerMode.isLoading = false;
    this.isLoadingTextures = false;
    this.isBaseTileLoading = false;
    this.screenLoadingCounter = 0;

    this.baseLoadingStateCallback = (isLoading: boolean) => {
      this.isBaseTileLoading = isLoading;
      this.updateViewerLoadingState();
    };

    this.textureLoadingStateCallback = (isLoading: boolean) => {
      this.isLoadingTextures = isLoading;
    };

    this.threeSixtyViewer.onLoadingStateChange(this.baseLoadingStateCallback);
    this.threeSixtyViewer.onTextureLoadingStateChange(this.textureLoadingStateCallback);

    ThreeSixtyViewer.notifyLoadingState = (isLoading, screen) => {
      if (screen !== ViewerScreenDirection.RIGHT) {
        return;
      }
      if (this.isBaseTileLoading) {
        return;
      }
      if (isLoading) {
        this.screenLoadingCounter += 1;
      } else if (this.screenLoadingCounter > 0) {
        this.screenLoadingCounter -= 1;
      }
      this.updateViewerLoadingState();
    };
  }

  private updateViewerLoadingState(): void {
    this.viewerMode.isLoading = this.isBaseTileLoading || this.screenLoadingCounter > 0;
  }

  public isViewerSettingEnabled(name: string, fallback = false): boolean {
    const stored = this.viewerSettings[name];
    const normalized = this.normalizeToggleValue(stored);
    if (normalized === undefined) {
      return fallback;
    }
    return normalized;
  }
  public isBlurFacesEnabled(): boolean {
    return this.isViewerSettingEnabled('toggle_blur_faces', false);
  }
  private syncViewerSettingsFromProject(): void {
    const organizationSettings =
      this.projectDetails?.organization_settings ??
      (this.projectDetails as any)?.organizationSettings;
    if (!organizationSettings) {
      this.viewerSettings = {};
      this.applyViewerSettings();
      return;
    }
    this.viewerSettings = { ...organizationSettings };
    this.applyViewerSettings();
  }
  private normalizeToggleValue(value: unknown): boolean | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const lower = value.toLowerCase();
      if (lower === '1' || lower === 'true') {
        return true;
      }
      if (lower === '0' || lower === 'false') {
        return false;
      }
    }
    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
    }
    return undefined;
  }

  public getViewerSetting<T = unknown>(name: string, fallback?: T): T | undefined {
    const stored = this.viewerSettings[name] as T | undefined;
    if (stored === undefined) {
      return fallback;
    }
    return stored;
  }

  private applyViewerSettings(): void {
    if (!this.threeSixtyViewer) return;
    const watermarkEnabled = this.isViewerSettingEnabled('toggle_watermark', false);
    this.threeSixtyViewer.setWatermarkEnabled(watermarkEnabled);
    this.applyWatermarkToViewer();
  }
  private applyWatermarkToViewer(): void {
    if (!this.threeSixtyViewer) return;
    let name = '';
    let email = '';
    let client = '';
    try {
      const stored = localStorage.getItem('user-data');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.name) {
          name = parsed.name;
        }
        if (parsed?.email) {
          email = parsed.email;
        }
        if (parsed?.client) {
          client = parsed.client;
        }
      }
    } catch (error) {
      name = '';
      email = '';
      client = '';
    }
    this.threeSixtyViewer.setWatermark(name, email, client);
  }
  public showMessage(title: string, message: string) {
    const dialogData = new ConfirmDialogModel(
      title,
      message,
      false,
      false,
      '',
      '',
      this._translocoService.translate('Close'),
      '',
      true,
    );
    this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '350px',
      minWidth: '300px',
      data: dialogData,
    });
  }
  public syncViewerWithBim() {
    this.threeSixtyViewer.addCameraMoveListener(this.bimCameraSyncCallBack, true);
  }
  public asyncViewerWithBim() {
    this.threeSixtyViewer.removeCameraMoveListener(this.bimCameraSyncCallBack, true);
  }

  setImageOreintation(
    reportId: string,
    pImageId: string,
    horizontalAngle: number,
    verticalAngle: number,
  ) {
    let body = {
      report_id: reportId,
      point_image_id: pImageId,
      horizontal_angle: horizontalAngle,
      vertical_angle: verticalAngle,
    };

    this.endPoint.post('set_default_point_image_direction', body).subscribe(
      async (response) => {
        this.threeSixtyViewer.setDefaultCameraOrientation({
          horizontalAngle: horizontalAngle,
          verticalAngle: verticalAngle,
        });
        this._toaster.success(
          this._translocoService.translate('Image direction updated successfully'),
        );
      },
      (error) => {
        this.global.handleErrors(error);
      },
    );
  }

  public setWalkThroughPoint(mapPoint: MapPoint, callback?: (pointId: String) => void) {
    const walkthroughs = this.mapNavigationHotspotToWalkthrough(mapPoint.navigation_hotspots);
    this.threeSixtyViewer.initWalkthroughes(walkthroughs, (id) => {
      callback(id);
    });
  }
  mapNavigationHotspotToWalkthrough(navigationHotspot: NavigationHotspot[]): Walkthrough[] {
    return navigationHotspot.map(
      (nav) =>
        new Walkthrough(
          new THREE.Vector2(Number(nav.ath), Number(nav.atv)),
          nav.id,
          nav.next_point_uuid,
          this.demoPointsService.getPointImageNameById(nav.next_point_uuid) || '',
        ),
    );
  }
  getPermission(access: AccessLevel): boolean {
    return !!this.projectDetails?.permissions?.includes(access);
  }

  getUserRole(role: string) {
    return this.projectDetails?.user_project_role === role;
  }

  async getImageUrlByPointId(pointId: string): Promise<String> {
    const pointImage = await this._project.getPImgFrom(
      this.selectedReport.id,
      this.selectedFloor.id,
      pointId,
    );
    const assets = this.demoPointsService.getPointImageAssets(pointImage, this.viewerSettings);
    return assets.imageUrl || 'assets/webglViewer/space.png';
  }

  public async updateQueryParams(params: {
    floor_id?: string;
    report_id?: string;
    point_id?: string;
  }): Promise<void> {
    const queryParams = { ...this.route.snapshot.queryParams };

    if (params.floor_id) queryParams['floor_id'] = params.floor_id;
    if (params.report_id) queryParams['report_id'] = params.report_id;
    if (params.point_id) queryParams['point_id'] = params.point_id;
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  toggleBimSplit() {
    this.googleServices.setEvent('SplitScreenBim_project', 'Viewer Interactivity');
    this.viewerMode.toggleBimSplit();
    this.bimService.currentFloor = this.selectedFloor as Floor;
    this.bimService.currentReport = this.selectedReport;
    this.threeSixtyViewer.toggleBimSScreen();
    if (this.viewerMode.isBimSplit) {
      // Entering BIM split: start in LOCKED state and sync immediately
      this.viewerMode.isBimLocked = true;
      this.viewerMode.isBimSplitBootstrapping = true;
      this.initialHorizAngleDeg = null; // clear any previous unlock baseline
      this.syncViewerWithBim();
    } else {
      // Leaving BIM split: unlock and stop syncing
      this.viewerMode.isBimLocked = false;
      this.viewerMode.isBimSplitBootstrapping = false;
      this.asyncViewerWithBim();
    }
  }

  async toggleBimLock(): Promise<boolean> {
    if (this.selectedReport.report_type !== REPORT_TYPE.IMAGE) return this.viewerMode.isBimLocked;

    const user = await this.userService.getCurrentUser();
    if (!this._project.bimCompareTourSeen?.includes(user.email)) {
      this.openBimCompareGuide();
      return this.viewerMode.isBimLocked;
    }

    // Currently locked -> unlock (capture initial angle, unsync)
    if (!this.viewerMode.isBimLocked) {
      const ori = this.threeSixtyViewer.getCameraOrientation();
      this.initialHorizAngleDeg = ori?.horizontalAngle ?? 0;
      // this.viewerMode.isBimLocked = false;
      this.asyncViewerWithBim();
      return false;
    }

    // Currently unlocked -> lock (compute offset, save, resync)
    const dialogRef = this.dialog.open(SaveBimOffsetDialogComponent, { width: '450px' });

    return new Promise<boolean>((resolve) => {
      dialogRef.afterClosed().subscribe((res) => {
        if (!res) {
          // User canceled saving offset -> revert the toggle back to unlocked
          this.viewerMode.toggleBimLock(); // revert to false
          this.asyncViewerWithBim();
          resolve(false);
          return;
        }

        const ori = this.threeSixtyViewer.getCameraOrientation();
        const currentHorizDeg = ori?.horizontalAngle ?? 0;
        const currentVertDeg = ori?.verticalAngle ?? 0;

        // Calculate the 360 viewer's target point from its current angles
        const mapPoint = this.currentRightPointImage;
        const bimPosition = new THREE.Vector3(
          parseFloat(mapPoint.bim_point_x),
          parseFloat(mapPoint.bim_point_y),
          parseFloat(mapPoint.bim_point_z),
        );

        const floorRotation = (this.selectedFloor as Floor)?.rotation_angle_degrees
          ? parseFloat((this.selectedFloor as Floor).rotation_angle_degrees)
          : 0;
        const headingAngle = mapPoint.heading_angle || 0;

        const viewer360Target = this.calculateTargetFromAngles(
          bimPosition,
          currentHorizDeg,
          currentVertDeg,
          headingAngle,
          floorRotation,
        );

        // Get BIM viewer's current camera target
        const bimCamera = this.bimService.bimViewer?.navigation?.getCamera();
        const bimTarget = bimCamera?.target?.clone();

        // Calculate the angle offset between the two targets
        const deltaDeg = bimTarget
          ? this.calculateBimOffsetAngle(bimPosition, viewer360Target, bimTarget)
          : 0;

        const reportId = this.selectedReport?.id;
        const pImageId = this.currentRightPointImageId;

        const finalizeLock = () => {
          this.initialHorizAngleDeg = null;
          this.syncViewerWithBim(); // start syncing
          resolve(true);
        };

        if (reportId && pImageId) {
          this.setBimOffsetAngle(reportId, pImageId, deltaDeg, res.is_default).finally(
            finalizeLock,
          );
        } else {
          finalizeLock();
        }
      });
    });
  }

  normalizeAngle(angle: number) {
    while (angle > 180) angle -= 360;
    while (angle < -180) angle += 360;
    return angle;
  }

  calculateTargetFromAngles(
    position: THREE.Vector3,
    hlookat: number,
    vlookat: number,
    headingAngle: number,
    floorRotation: number,
  ): THREE.Vector3 {
    const rotationAngle = floorRotation;
    const normalizedHeading = this.normalizeAngle(headingAngle);

    let hlookatRad: number;
    let dx: number;
    let dy: number;
    const vlookatRad = -vlookat * (Math.PI / 180);
    const headingRad = -normalizedHeading * (Math.PI / 180);

    if (this.selectedReport?.report_type === REPORT_TYPE.IMAGE) {
      hlookatRad = -hlookat * (Math.PI / 180);
      dx = Math.cos(vlookatRad) * Math.cos(hlookatRad);
      dy = Math.cos(vlookatRad) * Math.sin(hlookatRad);
    } else {
      hlookatRad = (-hlookat + rotationAngle) * (Math.PI / 180);
      dx = Math.cos(vlookatRad) * Math.cos(hlookatRad + headingRad);
      dy = Math.cos(vlookatRad) * Math.sin(hlookatRad + headingRad);
    }

    const dz = Math.sin(vlookatRad);

    return new THREE.Vector3(position.x + dx, position.y + dy, position.z + dz);
  }

  calculateBimOffsetAngle(
    cameraPosition: THREE.Vector3,
    target1: THREE.Vector3,
    target2: THREE.Vector3,
  ): number {
    // Step 1: Calculate the direction vectors from the camera position to each target
    let direction1 = new THREE.Vector3().subVectors(target1, cameraPosition);
    let direction2 = new THREE.Vector3().subVectors(target2, cameraPosition);

    // Step 2: Calculate the dot product of the two direction vectors
    const dotProduct = direction1.dot(direction2);

    // Step3 : Calculate the magnitude (lengths) of the two direction vectors
    const magnitude1 = direction1.length();
    const magnitude2 = direction2.length();

    // Step 4: Calculate the cosine of the angle
    const cosTheta = dotProduct / (magnitude1 * magnitude2);

    // Step 5: Clamp the cosine to avoid numerical errors and calculate the angle in radians
    const clampedTheta = Math.max(-1, Math.min(1, cosTheta));
    const angleInRadians = Math.acos(clampedTheta);

    // Step 6: Convert the angle from radians to degrees
    const angleInDegrees = (angleInRadians * 180) / Math.PI;

    return angleInDegrees;
  }

  async getPointImagesByFloor(
    reportID: any,
    floorID: any,
    force: boolean = false,
    replace: boolean = false,
  ) {
    // for fallback in case we dont get report id or floor id 360 viewer should not stop.
    if (!reportID) {
      reportID = this.projectDetails.reports[0].id;
    }

    let report = this.projectDetails.reports.find((rep) => rep.id === reportID);

    if (!floorID) {
      let floor = report.report_floors.find((fl) => fl.status == 'PUBLISHED_BY_OPERATION_ENG');
      if (!floor) this.projectDetails.floors[0];
      floorID = floor.floor_id;
    }

    if (force) {
      this.demoPointsService.pointImages = [];
    }

    let point = this.demoPointsService.pointImages.find((p) => {
      p.report_id === reportID && p.floor_id === floorID;
    });

    if (point && !replace) {
      return;
    }

    let points: any;
    if (report.report_type == REPORT_TYPE.VIDEOS) {
      points = await this.projectApi.getFullPath(report.id, false, floorID).toPromise();
    } else {
      points = await this.projectApi.getPointImagesByFloorId(report.id, floorID).toPromise();
    }

    const newImages = points.data;

    if (replace) {
      // if replace is true, replace existing images new ones
      newImages.forEach((newImage) => {
        const existingIndex = this.demoPointsService.pointImages.findIndex((existingImage) => {
          existingImage.id === newImage;
        });
        if (existingIndex !== -1) {
          this.demoPointsService.pointImages[existingIndex] = newImage;
        } else {
          this.demoPointsService.pointImages.push(newImage);
        }
      });
    } else {
      // If replace is false, skip existing images and only add new ones
      this.demoPointsService.pointImages.concat(
        newImages.filter((newImage) => {
          return !this.demoPointsService.pointImages.some(
            (existingImage) => existingImage.id === newImage.id,
          );
        }),
      );
    }
  }

  async setBimOffsetAngle(
    reportId: string,
    pImageId: string,
    bim_offset: number,
    is_default: boolean,
  ) {
    // Normalize to a signed angle in [-180, 180] to preserve rotation direction
    const normalizedAngle = this.normalizeAngle(bim_offset);
    let body = {
      report_id: reportId,
      point_image_id: pImageId,
      bim_offset: normalizedAngle,
      is_default: is_default,
    };

    this.endPoint.post('set-point-bim-offset', body, null, 'floors').subscribe(
      async (response) => {
        await this.getPointImagesByFloor(reportId, this.selectedFloor.id, false, true);
        const newOffset = normalizedAngle;
        if (!is_default) {
          this.currentRightPointImage.bim_offset = newOffset;
        } else {
          if (this.currentRightPointImage.bim_offset !== undefined) {
            this.currentRightPointImage.bim_offset = newOffset;
            // this.currentRightPointImage.default_bim_offset = newOffset;
          } else {
            // this.currentRightPointImage.default_bim_offset = newOffset;
          }
        }

        this._toaster.success(
          this._translocoService.translate('The direction has been adjusted successfully'),
        );
      },
      (error) => {
        console.error('An error occurred:', error);
        this._toaster.error(error.message);
      },
    );
  }

  openBimCompareGuide() {
    const setOrientationTour: TourGuideModel = {
      height: '700px',
      title: this._translocoService.translate('BIM compare with image reports guide'),
      subtitle: this._translocoService.translate(
        'In split screen mode, the 360 viewer and BIM viewers are synced to provide a cohesive viewing experience. If you encounter any issues with the sync, follow the steps below to manually adjust the views and restore proper alignment.',
      ),
      bullets: [
        {
          value: this._translocoService.translate(
            'Click the Unlock button to manually adjust the view',
          ),
        },
        {
          value: this._translocoService.translate(
            'Navigate the BIM viewer (on the left side) to the correct target or position.',
          ),
        },
        {
          value: this._translocoService.translate(
            'Once the BIM viewer is correctly aligned, click the Lock button to re-enable syncing between both viewers',
          ),
        },
        {
          value: this._translocoService.translate(
            'After locking, check that both the 360 viewer and BIM viewers are correctly synced and responding to interactions.',
          ),
        },
        {
          value: this._translocoService.translate(
            'After locking the screens, if you still notice a syncing issue between the viewers, it’s likely that the BIM viewer is flipped or rotated by 180 degrees. You can fix this by pressing the Flip button at the top right in the BIM viewer.',
          ),
        },
      ],
      image: 'assets/tour-guides/bim_tour.gif',
    };

    const dialogsRef = this.dialog.open(TourGuideDialogComponent, {
      data: setOrientationTour,
      autoFocus: false,
    });
    dialogsRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const user = await this.userService.getCurrentUser();
        this._project.setBimCompareTourSeen(user.email);
      }
    });
  }

  public transformDate(
    value: any,
    inputFormat: string = 'YYYY-MM-DDTHH:mm:ss.SSSSSSZ',
    dateOnly: boolean = false,
    dateWithTime: boolean = false,
  ): string {
    if (!value) {
      return localStorage.getItem('lang') === 'ar' ? 'غير محدد' : 'N/A';
    }

    // Format the date using Moment
    const formattedDate = moment(value, inputFormat).isValid()
      ? moment(value, inputFormat).format('YYYY-MM-DD HH:mm A')
      : '';

    if (!formattedDate) return '';

    // Guess the browser's timezone
    const timeZone = moment.tz.guess();

    // Convert to local timezone
    const localFormattedDate = moment.utc(value, inputFormat).tz(timeZone);

    const date = localFormattedDate.format('YYYY-MM-DD');
    const time = localFormattedDate.format('hh:mm');
    let ampm = localFormattedDate.format('A'); // AM/PM

    // Handle Arabic localization
    if (localStorage.getItem('lang') === 'ar') {
      ampm = ampm === 'AM' ? 'ص' : 'م';
    }

    if (dateOnly) {
      return date;
    }

    if (dateWithTime) {
      return `${date} ${time} ${ampm}`;
    }

    return `${date} <small class="whitespace-nowrap">${time} ${ampm}</small>`;
  }
  public reset() {
    this.reports = [];
    this.projectId = null;
    this.defaultReportId = '';
    this.defaultFloorId = '';
    this.selectedFloor = null;
    this.selectedReport = null;
    this.selectedLeftReport = null;
    this.projectDetails = null;
    this.viewerSettings = {};
    this.projectReports = [];
    this.reportsMeta = null;

    // Clean up loading state tracking
    if (this.threeSixtyViewer) {
      if (this.baseLoadingStateCallback) {
        this.threeSixtyViewer.removeLoadingStateCallback(this.baseLoadingStateCallback);
      }
      if (this.textureLoadingStateCallback) {
        this.threeSixtyViewer.removeTextureLoadingStateCallback(this.textureLoadingStateCallback);
      }
    }
    this.baseLoadingStateCallback = undefined;
    this.textureLoadingStateCallback = undefined;
    this.isLoadingTextures = false;
    this.isBaseTileLoading = false;
    this.screenLoadingCounter = 0;
    ThreeSixtyViewer.notifyLoadingState = undefined;

    this.threeSixtyViewer.destroy();
    this.demoPointsService.clearPoints();
    this.pointMatchService.clear();
    this.threeSixtyViewer = null;
  }
}
